/*
 * Modal — Figma Popup frame 119:45183 (values from get_design_context / get_variable_defs).
 *
 * | Part         | Node       | Spec                                                                                          |
 * | Overlay      | 119:45183  | rgba(16,16,19,.55) (--overlay), content centred                                                |
 * | Dialog       | 119:45184  | 840 wide (size md), --surface-raised, radius 12 (--radius-xl), clip, --shadow-dialog           |
 * | Header       | 119:45185  | padding 32 (x: --layout-page-padding-x, y: --space-2xl), bottom 1px --border-default           |
 * |  title       | 119:45187  | Mona Sans SemiBold 600, 22/28, tracking -0.3px, --text-default                                  |
 * |  close       | 119:45188  | IconButton secondary (32×32, padding 4/8, radius 6, --interaction-selected, secondary shadow), top 16 right 16 |
 * | Body         | 119:45189  | padding 48 32 (y: --layout-page-padding-y, x: --layout-page-padding-x)                          |
 * | Footer       | 119:45216  | padding 24 32 32, top 1px --border-default, items centred, space-between                       |
 *
 * Not in the frame: size xl (1080 wide, for detail views), the eyebrow/subtitle header slots,
 * the title's right padding (keeps a long title clear of the close button), the scrolling
 * body, and full screen below 640px (16px side padding there; header and footer stay put while
 * the body scrolls between them, the footer clear of the phone's home indicator).
 *
 * Behaviour: role="dialog", aria-modal, labelled by the title; focus moves into the dialog,
 * Tab is trapped and the rest of the page is inert; Esc, the close button and a click on
 * the overlay close it; focus returns to the element that had it; the page doesn't scroll
 * behind it. CSP-safe: no <style> elements (the scroll lock sets properties through CSSOM).
 */
import { useEffect, useId, useLayoutEffect, useRef } from 'react'
import type { KeyboardEvent, ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './cx'
import { IconButton } from './IconButton'
import { x16Close } from './icons'

export type ModalSize = 'md' | 'xl'

export interface ModalProps {
    open: boolean
    onClose: () => void
    /** Dialog title; also its accessible name. */
    title: ReactNode
    /** Accessible label of the close button. */
    closeLabel: string
    size?: ModalSize
    /** Header content above the title (e.g. badges). */
    eyebrow?: ReactNode
    /** Header content below the title (e.g. codes, location). */
    subtitle?: ReactNode
    footer?: ReactNode
    children: ReactNode
    /** Element to focus when the dialog opens; default the dialog itself. */
    initialFocus?: RefObject<HTMLElement | null>
    /** Keys pressed anywhere inside the dialog (shortcuts). Esc is handled by the modal. */
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
    /** id of an element describing the dialog. */
    describedBy?: string
}

const WIDTH: Record<ModalSize, string> = { md: 'w-[840px]', xl: 'w-[1080px]' }

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',')

function focusables(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.closest('[inert], [hidden]') && (typeof el.checkVisibility !== 'function' || el.checkVisibility()),
    )
}

// Open modals, innermost last: only the top one reacts to Esc and keeps focus.
const stack: HTMLElement[] = []

/** Locks the page's scroll while at least one modal is open (keeps the scrollbar's width). */
let locks = 0
let saved: { overflow: string; paddingRight: string } | null = null
function lockScroll() {
    if (locks++ > 0) return
    const root = document.documentElement
    const scrollbar = window.innerWidth - root.clientWidth
    saved = { overflow: root.style.overflow, paddingRight: document.body.style.paddingRight }
    root.style.overflow = 'hidden'
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`
}
function unlockScroll() {
    if (--locks > 0 || !saved) return
    document.documentElement.style.overflow = saved.overflow
    document.body.style.paddingRight = saved.paddingRight
    saved = null
}

export function Modal({ open, onClose, title, closeLabel, size = 'md', eyebrow, subtitle, footer, children, initialFocus, onKeyDown, describedBy }: ModalProps) {
    const titleId = useId()
    const overlayRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const pressStartedOnOverlay = useRef(false)
    const onCloseRef = useRef(onClose)
    useLayoutEffect(() => {
        onCloseRef.current = onClose
    })

    // Focus in, page inert and scroll-locked; focus back to the opener on close.
    useEffect(() => {
        if (!open) return
        const overlay = overlayRef.current
        const panel = panelRef.current
        if (!overlay || !panel) return
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
        stack.push(panel)
        lockScroll()
        const inerted: Element[] = []
        for (const sibling of Array.from(document.body.children)) {
            if (sibling === overlay || sibling.hasAttribute('inert') || sibling.tagName === 'SCRIPT') continue
            sibling.setAttribute('inert', '')
            inerted.push(sibling)
        }
        ;(initialFocus?.current ?? panel).focus({ preventScroll: true })

        const onDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key !== 'Escape' || stack[stack.length - 1] !== panel || event.defaultPrevented) return
            event.preventDefault()
            onCloseRef.current()
        }
        // Anything that moves focus out (a script, a click on the page) brings it back.
        const onFocusIn = (event: FocusEvent) => {
            if (stack[stack.length - 1] === panel && event.target instanceof Node && !panel.contains(event.target)) panel.focus({ preventScroll: true })
        }
        document.addEventListener('keydown', onDocumentKeyDown)
        document.addEventListener('focusin', onFocusIn)
        return () => {
            document.removeEventListener('keydown', onDocumentKeyDown)
            document.removeEventListener('focusin', onFocusIn)
            for (const el of inerted) el.removeAttribute('inert')
            stack.splice(stack.indexOf(panel), 1)
            unlockScroll()
            if (opener?.isConnected) opener.focus({ preventScroll: true })
        }
    }, [open, initialFocus])

    if (!open) return null

    const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab' && panelRef.current) {
            const items = focusables(panelRef.current)
            if (items.length === 0) {
                event.preventDefault()
                return
            }
            const first = items[0]!
            const last = items[items.length - 1]!
            const active = document.activeElement
            if (event.shiftKey && (active === first || active === panelRef.current)) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && active === last) {
                event.preventDefault()
                first.focus()
            }
            return
        }
        onKeyDown?.(event)
    }

    return createPortal(
        <div
            ref={overlayRef}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--overlay)] p-[16px] max-sm:p-0"
            onMouseDown={(event) => {
                pressStartedOnOverlay.current = event.target === event.currentTarget
            }}
            onClick={(event) => {
                if (event.target === event.currentTarget && pressStartedOnOverlay.current) onClose()
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={describedBy}
                tabIndex={-1}
                onKeyDown={onPanelKeyDown}
                className={cx(
                    'relative flex max-h-[calc(100dvh-32px)] max-w-[calc(100vw-32px)] flex-col overflow-clip rounded-[12px] bg-[var(--surface-raised)] shadow-[var(--shadow-dialog)] outline-none',
                    'max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none',
                    WIDTH[size],
                )}
            >
                <header className="flex w-full shrink-0 flex-col items-start gap-[8px] border-b border-solid border-[var(--border-default)] py-[32px] pr-[64px] pl-[32px] max-sm:px-[16px] max-sm:pt-[24px] max-sm:pr-[56px]">
                    {eyebrow}
                    <h2 id={titleId} className="m-0 text-[22px] leading-[28px] font-semibold tracking-[-0.3px] text-[var(--text-default)] [word-break:break-word]">
                        {title}
                    </h2>
                    {subtitle}
                </header>
                <div className="absolute top-[16px] right-[16px] flex max-sm:top-[12px] max-sm:right-[12px]">
                    <IconButton variant="secondary" icon={x16Close} label={closeLabel} onClick={onClose} />
                </div>
                <div className="min-h-0 w-full flex-1 overflow-y-auto px-[32px] py-[48px] max-sm:px-[16px] max-sm:py-[24px]">{children}</div>
                {footer && (
                    <footer className="flex w-full shrink-0 flex-wrap items-center justify-between gap-[8px] border-t border-solid border-[var(--border-default)] px-[32px] pt-[24px] pb-[32px] max-sm:px-[16px] max-sm:pt-[16px] max-sm:pb-[max(16px,env(safe-area-inset-bottom))]">
                        {footer}
                    </footer>
                )}
            </div>
        </div>,
        document.body,
    )
}
